import { FormEvent, useEffect, useState } from 'react';
import mark from './assets/echosol-mark.png';
import { Header } from './components/Header';
import { Icon, type IconName } from './components/Icons';
import { PrivacyPanel } from './components/PrivacyPanel';
import { ProductPreview } from './components/ProductPreview';
import { productPages, type ProductPageKind, type ProductSlug } from './productPages';

const companyEmail = 'contact@echosol.co.uk';

function getDummyPageRoute(pathname: string): DummyPageRoute | null {
  const normalisedPath = pathname.replace(/\/+$/, '') || '/';
  const match = normalisedPath.match(/^\/(bubbles|group-upp)\/(privacy|support|terms)$/);

  if (!match) return null;

  return {
    productSlug: match[1] as ProductSlug,
    pageKind: match[2] as ProductPageKind,
    path: normalisedPath,
  };
}

type DummyPageRoute = {
  productSlug: ProductSlug;
  pageKind: ProductPageKind;
  path: string;
};

const principles: Array<{ number: string; icon: IconName; title: string; copy: string }> = [
  {
    number: '01',
    icon: 'group',
    title: 'Connection, with a reason',
    copy: 'We design small shared rituals that give people something worth returning to—and something natural to talk about.',
  },
  {
    number: '02',
    icon: 'spark',
    title: 'Playfulness with purpose',
    copy: 'Competition can bring energy without taking over. Our products make achievement social, light and genuinely enjoyable.',
  },
  {
    number: '03',
    icon: 'shield',
    title: 'Care built into the system',
    copy: 'Privacy, moderation, accessibility and dependable engineering are product decisions—not a layer added at the end.',
  },
];

const productFoundations: Array<{ icon: IconName; title: string; copy: string }> = [
  { icon: 'heart', title: 'Social by design', copy: 'Private groups, weekly prompts, posts, voting and reactions create a shared rhythm.' },
  { icon: 'puzzle', title: 'A reason to return', copy: 'Daily practice and one shared race turn spare moments into friendly competition.' },
  { icon: 'layers', title: 'Progress together', copy: 'Day-to-day rankings and weekly group leaderboards make every result part of a bigger story.' },
  { icon: 'shield', title: 'Built for trust', copy: 'Reporting, blocking, account controls and careful audience boundaries support healthier groups.' },
];

function SectionHeading({ kicker, title, copy }: { kicker: string; title: string; copy?: string }) {
  return (
    <div className="section-heading reveal">
      <span className="eyebrow">{kicker}</span>
      <h2>{title}</h2>
      {copy ? <p>{copy}</p> : null}
    </div>
  );
}

function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subject = encodeURIComponent(`Website enquiry from ${name.trim() || 'a visitor'}`);
    const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\n${message}`);
    window.location.href = `mailto:${companyEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <form className="contact-form" onSubmit={submit}>
      <div className="field-row">
        <label>Name<input required autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Email<input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      </div>
      <label>What would you like to discuss?<textarea required rows={5} value={message} onChange={(event) => setMessage(event.target.value)} /></label>
      <div className="form-footer">
        <p>Your email application will open with this message ready to send.</p>
        <button className="button button--light" type="submit">Open email draft <Icon name="arrow" /></button>
      </div>
    </form>
  );
}

function DummyProductPage({ route }: { route: DummyPageRoute }) {
  const page = productPages[route.productSlug][route.pageKind];
  const navLinks = (['privacy', 'support', 'terms'] as ProductPageKind[]).map((kind) => ({
    kind,
    label: productPages[route.productSlug][kind].title,
    href: `/${route.productSlug}/${kind}`,
  }));

  useEffect(() => {
    document.title = `${page.product} ${page.title} | Echosol Solutions`;
  }, [page.product, page.title]);

  return (
    <>
      <Header />
      <main className="dummy-page" id="main-content">
        <section className="dummy-hero">
          <div className="shell dummy-hero__grid">
            <div>
              <span className="eyebrow">{page.eyebrow}</span>
              <h1>{page.title}</h1>
              <p>{page.summary}</p>
              <div className="dummy-actions">
                <a className="button" href={`mailto:${page.contactEmail}?subject=${encodeURIComponent(`${page.product} ${page.title}`)}`}>
                  Email Echosol <Icon name="mail" />
                </a>
                <a className="text-link" href="/#group-upp">Back to Echosol <span>↗</span></a>
              </div>
            </div>
            <aside className="dummy-card" aria-label="Page status">
              <span>{page.product}</span>
              <strong>{route.path}</strong>
              <p>Version {page.version} · Last updated {page.updated}</p>
              <nav className="dummy-page-nav" aria-label={`${page.product} legal pages`}>
                {navLinks.map((link) => (
                  <a className={route.pageKind === link.kind ? 'dummy-page-nav__active' : ''} href={link.href} key={link.kind}>{link.label}</a>
                ))}
              </nav>
            </aside>
          </div>
        </section>

        <section className="dummy-content section">
          <div className="shell dummy-section-grid">
            {page.sections.map((section) => (
              <article className="dummy-info-card" key={section.title}>
                <Icon name={route.pageKind === 'support' ? 'heart' : route.pageKind === 'terms' ? 'check' : 'shield'} />
                <h2>{section.title}</h2>
                {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="shell footer-legal">
          <p>© {new Date().getFullYear()} Echosol Solutions Ltd · Registered in Scotland · Company no. SC896357.</p>
          <div><a href="/">Echosol home</a><a href={`mailto:${page.contactEmail}`}>{page.contactEmail}</a></div>
        </div>
      </footer>
    </>
  );
}

export default function App() {
  const dummyRoute = getDummyPageRoute(window.location.pathname);
  const [privacyOpen, setPrivacyOpen] = useState(window.location.hash === '#privacy');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add('is-visible')),
      { threshold: 0.12 },
    );
    document.querySelectorAll('.reveal').forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const showPrivacy = () => {
    window.history.replaceState(null, '', '#privacy');
    setPrivacyOpen(true);
  };

  const hidePrivacy = () => {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    setPrivacyOpen(false);
  };

  if (dummyRoute) {
    return <DummyProductPage route={dummyRoute} />;
  }

  return (
    <>
      <Header />
      <main id="main-content">
        <section className="hero" id="top">
          <div className="hero-noise" />
          <div className="signal-lines" aria-hidden="true"><i /><i /><i /><i /></div>
          <div className="shell hero-grid">
            <div className="hero-copy">
              <div className="status-pill"><span /> Independent software company · Edinburgh</div>
              <h1>Technology should give people <em>more to share.</em></h1>
              <p>Echosol Solutions builds thoughtful digital products that turn everyday moments into real connection.</p>
              <div className="hero-actions">
                <a className="button" href="#group-upp">Discover Group Upp <Icon name="arrow" /></a>
                <a className="text-link" href="#approach">How we think <span>↓</span></a>
              </div>
            </div>
            <div className="hero-mark" aria-hidden="true">
              <span className="hero-mark__halo" />
              <img src={mark} alt="" />
              <div className="hero-note hero-note--one"><span>01</span> Social products</div>
              <div className="hero-note hero-note--two"><span>02</span> Playful systems</div>
              <div className="hero-note hero-note--three"><span>03</span> Thoughtful tech</div>
            </div>
          </div>
          <div className="shell company-strip">
            <div><strong>2026</strong><span>Established</span></div>
            <div><strong>Scotland</strong><span>Home base</span></div>
            <div><strong>Group Upp</strong><span>First product</span></div>
            <p>Founder-led and building for the long term.</p>
          </div>
        </section>

        <section className="manifesto section" id="approach">
          <div className="shell">
            <div className="manifesto-intro reveal">
              <span className="eyebrow">Why Echosol</span>
              <p className="manifesto-statement">The best technology doesn't demand the centre of attention. It creates a spark, then makes room for <em>people.</em></p>
            </div>
            <div className="principles">
              {principles.map((principle) => (
                <article className="principle-card reveal" key={principle.number}>
                  <div className="principle-top"><span>{principle.number}</span><Icon name={principle.icon} /></div>
                  <h3>{principle.title}</h3>
                  <p>{principle.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="product section" id="group-upp">
          <div className="shell product-grid">
            <div className="product-copy">
              <div className="product-brand reveal">
                <span className="product-brand__symbol"><Icon name="group" /></span>
                <span><small>Our first product</small><strong>Group Upp</strong></span>
              </div>
              <SectionHeading
                kicker="A shared reason to show up"
                title="A little competition. A lot more connection."
                copy="Group Upp is a mobile social and puzzle app built around private groups of people who already matter to each other. Weekly posts, daily puzzles and friendly rankings keep those groups active without asking users to perform for the whole internet."
              />
              <div className="product-status reveal"><span>Currently in beta</span><p>Testing, refining and preparing for release.</p></div>
              <a className="button button--dark reveal" href="#contact">Follow the journey <Icon name="arrow" /></a>
            </div>
            <div className="reveal reveal--visual"><ProductPreview /></div>
          </div>

          <div className="shell foundation-grid">
            {productFoundations.map((item) => (
              <article className="foundation-card reveal" key={item.title}>
                <Icon name={item.icon} />
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="craft section">
          <div className="shell craft-grid">
            <SectionHeading
              kicker="How we build"
              title="Small team. Complete product thinking."
              copy="Echosol brings the product, design and technical decisions together from the start. That keeps the experience coherent—from the first interaction to the systems running behind it."
            />
            <div className="craft-list">
              <article className="reveal"><span><Icon name="spark" /></span><div><h3>Product & interaction</h3><p>Clear ideas shaped into flows that feel intuitive, distinctive and worth coming back to.</p></div></article>
              <article className="reveal"><span><Icon name="braces" /></span><div><h3>Mobile & cloud engineering</h3><p>Reliable client experiences supported by secure, maintainable cloud systems.</p></div></article>
              <article className="reveal"><span><Icon name="shield" /></span><div><h3>Trust & responsibility</h3><p>Privacy, safety and operational controls considered as part of the product itself.</p></div></article>
            </div>
          </div>
          <div className="shell build-band reveal" aria-label="Echosol product qualities">
            <span>Human</span><i>✦</i><span>Playful</span><i>✦</i><span>Useful</span><i>✦</i><span>Responsible</span>
          </div>
        </section>

        <section className="about section" id="about">
          <div className="shell about-grid">
            <div className="about-art reveal" aria-hidden="true">
              <div className="about-art__panel"><img src={mark} alt="" /><span>Ideas that<br />travel outward.</span></div>
              <div className="about-art__label">Edinburgh · Scotland</div>
            </div>
            <div className="about-copy">
              <SectionHeading kicker="About us" title="Independent by design. Ambitious by nature." />
              <div className="reveal">
                <p>Echosol Solutions Ltd is a founder-led software company established in Edinburgh in 2026. We exist to build and operate original consumer products with a clear point of view.</p>
                <p>Our first focus is Group Upp: a new kind of private social space shaped around shared prompts, puzzles and the people you actually know. As the company grows, the principle stays the same—technology should support richer human moments.</p>
              </div>
              <dl className="company-facts reveal">
                <div><dt>Legal name</dt><dd>Echosol Solutions Ltd</dd></div>
                <div><dt>Company number</dt><dd>SC896357</dd></div>
                <div><dt>Registered in</dt><dd>Scotland</dd></div>
                <div><dt>Founded</dt><dd>18 July 2026</dd></div>
              </dl>
            </div>
          </div>
        </section>

        <section className="contact section" id="contact">
          <div className="shell contact-shell">
            <div className="contact-copy reveal">
              <span className="eyebrow eyebrow--light">Start a conversation</span>
              <h2>Have something<br />worth sharing?</h2>
              <p>For product questions, partnership conversations or general enquiries, we'd be glad to hear from you.</p>
              <a href={`mailto:${companyEmail}`}><Icon name="mail" /> {companyEmail}</a>
            </div>
            <div className="reveal"><ContactForm /></div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="shell footer-main">
          <a className="wordmark wordmark--footer" href="/#top" aria-label="Back to top"><img src={mark} alt="" /><span>Echosol<span>Solutions</span></span></a>
          <p>Thoughtful software for the way people connect.</p>
          <div className="footer-links">
            <a href="/#approach">Our approach</a><a href="/#group-upp">Group Upp</a><a href="/#about">About</a><a href="/#contact">Contact</a>
          </div>
        </div>
        <div className="shell footer-legal">
          <p>© {new Date().getFullYear()} Echosol Solutions Ltd · Registered in Scotland · Company no. SC896357 · Registered office: 11 East Preston Street, Edinburgh, Scotland, EH8 9QG.</p>
          <div><button type="button" onClick={showPrivacy}>Privacy</button><a href="https://find-and-update.company-information.service.gov.uk/company/SC896357" target="_blank" rel="noreferrer">Companies House ↗</a></div>
        </div>
      </footer>

      <PrivacyPanel open={privacyOpen} onClose={hidePrivacy} />
    </>
  );
}
